import { FC } from 'react'
import {
  CampaignsOverviewSources,
  CampaignsOverviewTopCampaign,
  CampaignsOverviewTopKeyword,
} from 'src/services/__generated__/api'
import { TopCampaignCard } from './TopCampaignCard'
import { TopKeywordCard } from './TopKeywordCard'
import { TopSourceCard } from './TopSourceCard'
import { TotalCampaignsCard } from './TotalCampaignsCard'
import { TotalMentionsCard } from './TotalMentionsCard'
import { TotalSourcesCard } from './TotalSourcesCard'
import styles from './cards.module.scss'

interface MentionsBreakdown {
  negativeCount: number
  negativePercentage: number
  neutralCount: number
  neutralPercentage: number
  positiveCount: number
  positivePercentage: number
  total: number
}

interface CardsProps {
  activeCampaigns: number
  isLoading: boolean
  mentions: MentionsBreakdown
  sourceCounts?: { name?: string; total?: number }[]
  sourcesList: string[]
  topCampaign?: CampaignsOverviewTopCampaign
  topKeyword?: CampaignsOverviewTopKeyword
  topSource?: CampaignsOverviewSources
  totalCampaigns: number
  viewMode?: 'general' | 'platform'
}

export const Cards: FC<CardsProps> = ({
  activeCampaigns,
  isLoading,
  mentions,
  sourceCounts,
  sourcesList,
  topCampaign,
  topKeyword,
  topSource,
  totalCampaigns,
}) => {
  const hasMentions = mentions.total > 0
  const sourcesUniqueCount = topSource?.uniqueCount ?? sourcesList.length

  return (
    <div className={styles.cards}>
      {/* `hasData` no longer absorbs isLoading. It used to read
          `isLoading || x > 0`, which forced the "has data" branch open during
          the first load and rendered 0s / 0% as real values. Each card now
          takes isLoading and shows skeletons, so hasData is purely about
          whether data actually exists and the empty state stays reachable. */}
      <TotalCampaignsCard
        activeCount={activeCampaigns}
        hasData={totalCampaigns > 0}
        isLoading={isLoading}
        totalCount={totalCampaigns}
      />
      <TotalMentionsCard
        hasData={hasMentions}
        isLoading={isLoading}
        negativeCount={mentions.negativeCount}
        negativePercentage={mentions.negativePercentage}
        neutralCount={mentions.neutralCount}
        neutralPercentage={mentions.neutralPercentage}
        positiveCount={mentions.positiveCount}
        positivePercentage={mentions.positivePercentage}
        total={mentions.total}
      />
      <TotalSourcesCard
        hasData={sourcesUniqueCount > 0}
        isLoading={isLoading}
        sourceCounts={sourceCounts}
        sources={sourcesList}
        uniqueCount={sourcesUniqueCount}
      />
      <TopCampaignCard
        hasData={Boolean(
          topCampaign?.mostPositive || topCampaign?.mostNegative
        )}
        isLoading={isLoading}
        mostNegative={topCampaign?.mostNegative}
        mostPositive={topCampaign?.mostPositive}
      />
      <TopKeywordCard
        hasData={Boolean(topKeyword?.mostPositive || topKeyword?.mostNegative)}
        isLoading={isLoading}
        mostNegative={topKeyword?.mostNegative}
        mostPositive={topKeyword?.mostPositive}
      />
      <TopSourceCard
        hasData={Boolean(topSource?.mostPositive || topSource?.mostNegative)}
        isLoading={isLoading}
        mostNegative={topSource?.mostNegative}
        mostPositive={topSource?.mostPositive}
      />
    </div>
  )
}
